﻿import { ParameterSchemaStation, ParameterSchemaSensor, ParameterInfo, ParameterVariant } from "../../API/Schema/ParameterSchema";
import { DefaultParameterDefinitions } from "../../DefaultParameterDefinitions";
import { MainSite } from "../../MainSite";
import { NoDownloadPermissionDialog } from "./NoDownloadPermissionDialog";
import { ParameterNodeDownloadHandler } from "./ParameterNodeDownloadHandler";
import { PlottingPage } from "./PlottingPage";

/** Flag used in generating Tree nodes, indicating whether SensorId's should be split (if they contain slashes) or not. */
const SPLIT_SENSOR_IDS: boolean = true;
/** Character used for splitting SensorId's when we want to generate multiple levels of sensor folders. */
const SPLIT_SENSOR_CHAR: string = '/';

export class ParameterTree {
    public tree: JQuery;
    public desiredResolution: number;
    public fancyTree: Fancytree.Fancytree;
    private initialResize: boolean;

    private $treeFilterInput: JQuery;
    private $treeFilterClearBtn: JQuery;

    public currentStationNodes: StationNodeData[] = [];

    constructor(private mainSite: MainSite, private plottingPage: PlottingPage) {
        //chart must be resized after the first parameter is selected
        this.initialResize = false;
        this.tree = $('<div class="midi_PlottingPage_series_panel_sideContainer_box overflow-auto flex-col-item--grow"></div>');

        this.currentStationNodes = this.getTreeNodes(); //: needed for object searches!

        var fancyTreeElement = (<JQuery><any>$('<div id="tree" class="midi_PlottingPage_series_panel_tree"></div>')
            .appendTo(this.tree)
            .fancytree({
                checkbox: true,
                autoScroll: true,
                renderNode: (event, data) => {
                    // $(data.node.span).attr('title', `key=${data.node.key || '--NO-KEY--'} - PATH=${data.node.data.path ?? '--NO-PATH--'} - sensorId=${data.node.data.sensorId ?? '--no-sensorId--'}`); //TODO: remove - this is for debug only!
                    $(data.node.span).attr('data-node-key', data.node.key);
                    $(data.node.span).attr('data-node-path', data.node.data.path ?? '--NO-PATH--');
                    $(data.node.span).attr('data-node-sensor', data.node.data.sensorId ?? '--no-sensorId--');
                    $(data.node.span).attr('data-node-type', data.node.data.NODE_TYPE);

                    this.renderNodeOverride(data.node);
                },
                source: this.currentStationNodes,
                select: (event, data) => {
                    this.nodeClicked(data.node); // for csv download will need to do nothing
                },
                click: (event, data) => {
                    // console.info('tree click', data);

                    if (!data.node.folder) {
                        data.node.toggleSelected();
                        return false;
                    }
                    return true;
                },
                clickFolderMode: 3,
                extensions: ["filter"],
                filter: {
                    autoApply: false,
                    autoExpand: true,
                    counter: false,
                    fuzzy: true,
                    hideExpanders: true,
                    highlight: true,
                    mode: 'hide',
                    nodata: true,
                }
            }));

        this.fancyTree = fancyTreeElement.fancytree('getTree');
        
        this.setupNodes(this.fancyTree.rootNode); // will need to modify not to download anything/remove the click

        this.tree.resize(() => {
            plottingPage.resizeChart();
        });    
    }

    public renderNodeOverride(node: Fancytree.FancytreeNode): void {
        //add buttons etc that we might need on specific nodes

        //forecast issue time download btn
        if (node.children) {
            let nodeSpan = $(node.span);
            let nodeHasGrandkids = false;

            for (let i = 0; i < node.children.length; i++) {
                if (node.children[i].children) {
                    nodeHasGrandkids = true;
                    break;
                }
            }

            let parts = node.key.split("|");

            if (!nodeHasGrandkids &&
                node.key.indexOf("Forecast") !== -1 && //must be a forecast
                parts.length === 3 && //need to have station, sensor and parameter IDs
                nodeSpan.find(".midi_plotting_tree_inline_btn").length === 0) {
                $('<div class="midi_plotting_tree_inline_btn">Add Issue</div>')
                    .click((e) => {
                        e.stopPropagation();
                        try {
                            parts[1] = node.data.sensorNode.sensorId;
                        } catch (err) {
                            console.warn(`[ADD ISSUE][ERROR] Unable to replace tree  key sensor id with actual sensor id.`, { error: err, parts: parts, node: node });
                        }

                        this.plottingPage.addIssue(parts[0], parts[1], parts[2], node);
                        return false;
                    })
                    .appendTo(nodeSpan);
            }

            if (node.data.VariantId) {
                nodeSpan.append("<span>" + node.data.VariantId + "</span>");
            }
        }
    }

    public setupTreeToolbar($destination: JQuery): void {
        var $wrap: JQuery = $('<div id="plot-params-tree--toolbar"></div>').css('marginTop', 12);

        var $ig: JQuery = $('<div class="input-group input-group-sm"></div>');
        this.$treeFilterInput = $('<input type="text" placeholder="Filter..." class="form-control">').appendTo($ig);
        this.$treeFilterClearBtn = $('<button class="btn btn-default"><i class="fa fa-lg fa-times"></i></button>').appendTo($('<span class="input-group-btn"></span>').appendTo($ig));

        $ig.appendTo($wrap);
        $wrap.appendTo($destination);

        var _this: ParameterTree = this;
        this.$treeFilterClearBtn.on("click", function (e) {
            _this.$treeFilterInput.val('');
            _this.fancyTree.clearFilter();
        });
        this.$treeFilterInput.on("keyup", function (e) {
            try { //: note try/catch primarily added to prevent page failures, for a non-essential feature. Also: using .replace() instead of .trim() as the latter may not be supported in older browsers. The .replace() regex was taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim
                if ($(this).val().replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '') == "") {
                    _this.fancyTree.clearFilter();
                    return;
                }
                _this.fancyTree.filterNodes($(this).val());
            } catch (err) {
                console.warn("ParameterTree filter error: ", err);
            }
        });
    }

    private setupNodes(node: Fancytree.FancytreeNode) {
        if (node.getLevel() == 1) {
            var rootNodeImg = $((<any>node).li).find('img')
            
            rootNodeImg
                .click(() => {
                    var parameterSchemaStation: ParameterSchemaStation = (<any>node.data).station;
                    if (this.plottingPage.checkIfUserCanDownloadStationData(parameterSchemaStation.Station.Id)) {
                        //permission
                        this.plottingPage.downloadData((<any>node.data).station);
                    } else{
                        new NoDownloadPermissionDialog().show();
                    }
                        return false;
                    })
                .addClass('midi_PlottingPage_ParameterTree_downloadLink');
        }

        if (node.children) {
            node.children.forEach((child: Fancytree.FancytreeNode) => {
                this.setupNodes(child);
            });
        }
    }

    private getTreeNodes(): StationNodeData[] {

        // Get staionsIds of the selected region
        const stationIds: string[] = Object.keys(this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations)
            .filter(stationId => this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId].Station.Region === this.plottingPage.mainSite.hashData.Region);

        // Get the corresponding stationLabels
        const stationLabels = stationIds.map(stationId => this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId].Station.Label)
        // Sort the stationLabels alphabetically
        const stationLabelsSorted = [...stationLabels].sort()

        //// Apply the order from alphabetically sorted stationLabels to the stationIds
        let stationIdsSorted = [];

        for (const stationLabel of stationLabelsSorted) {

            for (const stationId of stationIds) {
                if (this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId].Station.Label == stationLabel) {
                    stationIdsSorted = [
                        ...stationIdsSorted,
                        stationId
                    ]
                }
            }
        }

        var children: StationNodeData[] = [];

        stationIdsSorted.forEach((stationId: string) => {
            children.push(new StationNodeData(this.mainSite, this.plottingPage, stationId, this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId]));
        });

        return children;
    }

    private replacementForRenderTitle(
        node: Fancytree.FancytreeNode, 
        icon: string
    ): void {
        node.data.icon = icon;
        node.renderTitle();
    }

    private nodeClicked(node: Fancytree.FancytreeNode): void {
        var downloadHandler: ParameterNodeDownloadHandler = <ParameterNodeDownloadHandler>node.data;

        console.log("[nodeClicked()] downloadHandler: ", downloadHandler)

        if (!downloadHandler) {
            return;
        }

        try {
            if (node.isSelected()) {
                downloadHandler.showData((icon: string) => {
                    this.replacementForRenderTitle(node, icon)
                });
                if (!this.initialResize) {
                    this.plottingPage.resizeChart();
                    this.initialResize = true;
                }
            } else {
                downloadHandler.hideData((icon: string) => {
                    this.replacementForRenderTitle(node, icon)
                });
            }
        } catch (err) {
            console.warn("[ERROR][nodeClicked()]:", err);
        }
    }

    private getDownloadHandler(node: Fancytree.FancytreeNode): ParameterNodeDownloadHandler|null {
        var downloadHandler: ParameterNodeDownloadHandler = <ParameterNodeDownloadHandler>node.data;

        if (!downloadHandler) {
            return null;
        }

        return downloadHandler;
    }

    public updateAll(): void {
        this.plottingPage.delayUpdate = true;

        this.fancyTree.getSelectedNodes().forEach((node: Fancytree.FancytreeNode) => {
            this.nodeClicked(node);
        });
    }

    public getLatestDataAll(tsFrom: number, tsTo: number, resolution: number): void {
        this.plottingPage.delayUpdate = true;

        let nodes: Fancytree.FancytreeNode[] = this.fancyTree.getSelectedNodes();
        nodes.forEach((node: Fancytree.FancytreeNode, i: number) => {
            if (i == nodes.length - 1) {
                this.plottingPage.delayUpdate = false;
            }
            let dh: ParameterNodeDownloadHandler = this.getDownloadHandler(node);
            if (dh) {
                dh.refreshData(tsFrom, tsTo, resolution, (icon: string) => {
                    this.replacementForRenderTitle(node, icon)
                });
            }
        });
    }

    /**
     * Helper to read/search a flattened array of sensor nodes for a station or all stations (used in ParameterSetList for example)
     * 
     * @param stationNode
     * @param keysOnly
     * @returns
     * @example ```
     *      let p = Icon.Midi.WebApplication.FrontEnd.MainSite.instance.pages[2];
     *      let prelude = p.parameterTree.fancyTree.rootNode.children[6].data.node; //: if selected region is Australia
     *      p.parameterTree.flattenSensorNodesRecursive(prelude.data._self, true);
     * ```
     */
    public flattenSensorNodesRecursive(stationNode?: StationNodeData, keysOnly: boolean = false): any[] {
        if (stationNode) {
            return stationNode.flattenSensorNodesRecursive(keysOnly);
        } else {
            let all = [];
            this.currentStationNodes.forEach(x => {
                all = all.concat(this.flattenSensorNodesRecursive(x._self, keysOnly));
            });
            return all;
        }
    }
}

class StationNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'station';
    public key: string;
    public title: string;
    public data: string;
    public icon: string = '/images/icons/download.png';
    public extraClasses: string;
    public hideCheckbox: boolean = true;
    public folder: boolean = true;
    public _self: StationNodeData;

    public children: SensorNodeData[] = [];

    constructor(private mainSite: MainSite, public plottingPage: PlottingPage, public stationId: string, public station: ParameterSchemaStation) {
        this.title = station.Station.Label;
        this.key = `${station.Station.Id}`;

        var sensorIds: string[] = [];

        for (var sensorId in station.Sensors) {
            sensorIds.push(sensorId);
        }

        sensorIds.sort();

        sensorIds.forEach((sensorId: string) => {
            let sensorNode = new SensorNodeData(this.mainSite, this, sensorId, station.Sensors[sensorId]);
            //: NB: SensorNodeData now automatically adds necessary nodes to the tree.
        });

        this._self = this;
    }

    /**
     * Provides incremental keys/paths, which are necessary in case of grouping re-arranged sensor nodes together.
     * @param _chunk The current chunk in question, or if no chunking, the sensorId.
     * @param chunks The chunks of the sensor ID
     * @param chunkIndex The index of the current chunk provided as _chunk
     * @returns The compiled key in format `StationId|IncrementalSplitSensorId`
     */
    public buildSensorPath = (_chunk: string, chunks: string[] = null, chunkIndex: number = null): string => {
        let _key = `${this.key}|`;

        if (chunks && chunks.slice(0, chunkIndex).length > 0) {
            _key += `${chunks.slice(0, chunkIndex).join(SPLIT_SENSOR_CHAR)}${SPLIT_SENSOR_CHAR}`;
        }

        _key += _chunk;
        return _key;
    }

    /**
     * Helper to read/search a flattened array of sensor nodes for this station (helpful mainly for regrouping if that option is enabled).
     * @param pathsOnly
     * @returns
     */
    public flattenSensorNodesRecursive(pathsOnly: boolean = false): any[] {
        let res: any[] = [];

        this.children.forEach(x => {
            res.push(pathsOnly ? x.path : x);
            x.flattenChildSensorNodesRecursive(pathsOnly, res);
        });

        return res;
    }

    /**
     * Helper to get the node to which a sensor & params need adding. Idea is to use this to add parameters to the final node given as a result. Warning: this may return
     * the station node rather than a parameter node (as it's the root node for this action), but this is very unlikely to be the case in reality, unless autoAdd is not true.
     * 
     * @param chunks The chunks (or leaves) of the sensor ID, this should be at the very last one string corresponding to a sensor ID if it is not meant to be a tree of sensors.
     * @param autoAdd Automatically add the relevant nodes? This should normally always be true.
     * @param srcSensorId The source sensorId that triggered this call. If null, it will be built from chunks.
     * @param srcSensor The source sensor parameters that triggered this call. If null, a blank dummy ParameterSchemaSensor will be generated.
     * @returns The node to add sensor parameters to normally.
     */
    public getSensorNodeRecursively(chunks: string[], autoAdd: boolean = true, srcSensorId: string = null, srcSensor: ParameterSchemaSensor = null): SensorNodeData { // StationNodeData | 
        let debug = false;

        if (debug) {
            console.info('StationNodeData.getSensorNodeRecursively()', { chunks: chunks, srcSensor: srcSensor }); // , srcSensorId: srcSensorId
        }

        let finalNode: SensorNodeData = null;
        let addTo: any = this; //: progressively change this to each parent leaf when looping through chunks.. staring with station node to start with.

        chunks.forEach((chunk, i) => {
            let flat = this.flattenSensorNodesRecursive(false);
            let path = this.buildSensorPath(chunk, chunks, i);
            let incrementalChunks = chunks.slice(0, i + 1);

            let match: any[] = flat.filter(x => x.path == path);
            if (debug) {
                console.info('StationNodeData.getSensorNodeRecursively() - CHUNKS LOOP', { chunk: chunk, i: i, path: path, match: match, incrementalChunks: incrementalChunks });
            }

            if (match.length == 1) {
                finalNode = match[0];
                addTo = match[0];
            } else if (match.length == 0) {
                if (autoAdd) {
                    let _srcSensor: ParameterSchemaSensor = {
                        Parameters: {},
                        SensorDefinition: {
                            DisplayName: chunk,
                            Combined: false,
                            Level: null,
                            SourceIndex: null,
                            Hidden: false,
                        },
                        UseLiveData: true,
                    };

                    if (i == chunks.length - 1) {
                        if (srcSensor !== null) {
                            _srcSensor = srcSensor;
                        }
                    }

                    let sensorNode = new SensorNodeData(this.mainSite, this, srcSensorId, _srcSensor, this.buildSensorPath(incrementalChunks.join(SPLIT_SENSOR_CHAR)), chunk, false, false);
                    if (debug) {
                        console.info('StationNodeData.getSensorNodeRecursively() - CHUNKS LOOP - new SensorNodeData()', { sensorNode: sensorNode });
                    }
                    addTo.children.push(sensorNode);

                    addTo = sensorNode;
                    finalNode = sensorNode;
                } else {
                    console.warn(`StationNodeData.getSensorNodeRecursively() - ${match.length} matches found for path '${path}', and autoAdd param is false...`);
                }
            } else {
                console.warn(`StationNodeData.getSensorNodeRecursively() - ${match.length} matches found for path '${path}', when 1 or 0 expected. We're going to use the first result by default, but this may not be the wanted behaviour, in which case sensor IDs need to be checked.`);
                finalNode = match[0];
                addTo = match[0];
            }
        });

        if (debug) {
            console.info('StationNodeData.getSensorNodeRecursively() - FINAL NODE', { finalNode: finalNode, chunks: chunks });
        }

        return finalNode;
    }
}

class SensorNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'sensor';
    public key: string;
    public title: string;
    public hideCheckbox: boolean = true;
    public folder: boolean = true;
    public icon: string = '/images/icons/sensor.png';
    public isSplitSensor: boolean = false;
    public needsPushingToStationChildren = true;

    public readonly stationId: string; //: needed for ParameterSetList!

    public children: any[] = []; // ParameterNodeData[] = [];

    constructor(private mainSite: MainSite, public stationNode: StationNodeData, public sensorId: string, public sensor: ParameterSchemaSensor, public path: string = null, public label: string = null, allowHandlingParents: boolean = true, addParams: boolean = true) {
        this.title = (sensor.SensorDefinition && sensor.SensorDefinition.DisplayName) ? sensor.SensorDefinition.DisplayName : sensorId;
        if (this.label) {
            this.title = this.label;
        }
        this.key = `${stationNode.key}|${sensorId}`;

        this.stationId = stationNode.stationId;

        if (path == null) {
            this.path = stationNode.buildSensorPath(sensorId); //: this should maybe be built on either split display name or 
        }

        let addParamsTo: SensorNodeData = this;

        let sensorPathFinal = sensorId;
        if (sensor.SensorDefinition && sensor.SensorDefinition.DisplayName) {
            sensorPathFinal = sensor.SensorDefinition.DisplayName; //: initially, we can set paths in sensor DisplayName - if this is not working then a new DB column may need to be added.
        }
        let splitSensorId = sensorPathFinal.split(SPLIT_SENSOR_CHAR); //: TODO HERE: change this to use the database param we want to split (DisplayName ? or other if we feel using the display name for this as a temporary solution works..)

        //: emulate/test nested sensors for Prelude:
        /*
        if (stationNode.key == 'Australia_Prelude' && sensorId.substring(0, 'Forecast '.length) == 'Forecast ') {
            splitSensorId = sensorId.split(' ');
        }
        if (stationNode.key == 'Australia_Prelude' && sensorId == 'Forecast_30min') {
            splitSensorId = sensorId.split('_');
        }
        if (stationNode.key == 'Australia_Prelude' && sensorId == 'Forecast') {
            console.info('Australia_Prelude.Forecast  EMULATED TO  Forecast/Main/Tests...');
            splitSensorId = ['Forecast', 'Main', 'Tests'];
            // sensorId = 'Forecast/Main/Tests';
        }
        */

        if (SPLIT_SENSOR_IDS && allowHandlingParents) {
            addParamsTo = stationNode.getSensorNodeRecursively(splitSensorId, true, sensorId, sensor) ?? this;
        }

        if (!addParams) {
            // console.warn('SensorNodeData DO NOT ADD PARAMS set.', sensorId, this.key, this.path, addParamsTo);
            return;
        }

        var parameters: ParameterInfo[] = [];

        for (var parameterId in sensor.Parameters) {
            var parameterInfo: ParameterInfo = sensor.Parameters[parameterId];
            parameterInfo.Id = parameterId;

            if (!parameterInfo.Variants.some(v => {
                var parameterDefinition = v.ParameterDefinition;

                if (!parameterDefinition || parameterDefinition.Default) {
                    parameterDefinition = DefaultParameterDefinitions.Parameters[parameterId];
                }

                return !parameterDefinition || parameterDefinition.AllowPlot;
            })) {
                continue;
            }

            parameters.push(sensor.Parameters[parameterId]);
        }

        parameters.sort((a, b) => a.Id.localeCompare(b.Id));

        parameters.forEach((parameter) => {
            addParamsTo.children.push(new ParameterNodeData(this.mainSite, addParamsTo, parameter));
        });
    }

    public flattenChildSensorNodesRecursive(pathsOnly: boolean = false, addTo: any[] = []): void {
        this.children.forEach(x => {
            if (x.NODE_TYPE == 'sensor') {
                addTo.push(pathsOnly ? x.path : x);
                x.flattenChildSensorNodesRecursive(pathsOnly, addTo);
            }
        });
    }
}

class ParameterNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'parameter';
    public key: string;
    public title: string;
    public hideCheckbox: boolean;
    public folder: boolean;
    public icon: string;

    public readonly stationId: string; //: needed for ParameterSetList!

    public children: VariantNodeData[];

    public data: ParameterNodeDownloadHandler;

    constructor(private mainSite: MainSite, public sensorNode: SensorNodeData, public parameter: ParameterInfo) {
        this.title = (parameter.Variants.length && parameter.Variants[0].ParameterDefinition
            && parameter.Variants[0].ParameterDefinition.DisplayName)
            ? parameter.Variants[0].ParameterDefinition.DisplayName : parameter.Id;

        this.key = `${sensorNode.key}|${parameter.Id}`;

        this.stationId = sensorNode.stationId;

        if (parameter.Variants.length == 1 && parameter.Variants[0].Variant === null) {
            this.data = new ParameterNodeDownloadHandler(
                this.mainSite,
                sensorNode.stationNode.plottingPage,
                parameter.Store,
                this.sensorNode.stationNode.stationId,
                this.sensorNode.sensorId,
                parameter.Id,
                null);
            this.icon = '/images/icons/lineChartIcon.png';
            this.folder = false;
            this.hideCheckbox = false;
            this.children = null;
        } else {
            this.data = null;
            this.icon = '/images/icons/sensor.png';
            this.folder = true;
            this.hideCheckbox = true;
            this.children = parameter.Variants
                .filter(v => {
                    var parameterDefinition = v.ParameterDefinition;

                    if (!parameterDefinition || parameterDefinition.Default) {
                        parameterDefinition = DefaultParameterDefinitions.Parameters[parameter.Id];
                    }

                    return !parameterDefinition || parameterDefinition.AllowPlot;
                })
                .sort((a, b) => {
                    let av = parseFloat(a.Variant);
                    let bv = parseFloat(b.Variant);
                    if (!isNaN(av) && !isNaN(bv)) {
                        return av - bv;
                    } else if (isNaN(av)) {
                        return -1;
                    } else if (isNaN(bv)) {
                        return 1;
                    } else {
                        return a.Variant.localeCompare(b.Variant);
                    }
                })
                .map(v => new VariantNodeData(
                mainSite,
                this,
                v));
        }
    }
}

export class VariantNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'variant';
    public key: string;
    public title: string;
    public icon: string = '/images/icons/lineChartIcon.png';
    public data: ParameterNodeDownloadHandler;

    public readonly stationId: string; //: needed for ParameterSetList!

    constructor(private mainSite: MainSite, public parentNode: ParameterNodeData, public variant: ParameterVariant) {
        this.stationId = parentNode.stationId;

        this.title = variant.Variant === null ? "Latest" : variant.Variant;
        this.key = `${parentNode.sensorNode.key}|${parentNode.parameter.Id}|${variant.Variant}`; //: NB: "null" added if we receive variantName===null, which is necessary for handling matches with plot page tree selections and also downloads instructions sent to server.
        this.data = new ParameterNodeDownloadHandler(
            this.mainSite,
            parentNode.sensorNode.stationNode.plottingPage,
            parentNode.parameter.Store,
            parentNode.sensorNode.stationNode.stationId,
            parentNode.sensorNode.sensorId,
            parentNode.parameter.Id,
            variant.Variant);
    }
}

 